import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/config";
import { toast } from "sonner";
import { ChevronRight, Mail, Download, Users, Trash2, CheckCircle, UserPlus, Send, List, Tag } from "lucide-react";
import { API } from "@/config";
import { usePageTitle } from "@/hooks/usePageTitle";

const NewsletterAdminPage = ({ isAdmin, adminPassword }) => {
  usePageTitle('Newsletter Admin');
  const navigate = useNavigate();
  
  const [subscribers, setSubscribers] = useState([]);
  const [mailingLists, setMailingLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newLists, setNewLists] = useState(['general']);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);


  // Get password from props or localStorage
  const getAdminPassword = () => {
    if (adminPassword && adminPassword !== 'undefined' && adminPassword !== 'null') {
      return adminPassword;
    }
    return localStorage.getItem('spoleto_admin_auth') || localStorage.getItem('spoleto_admin_auth') || localStorage.getItem('adminToken') || localStorage.getItem('adminAuth') || '';
  };

  // Check authentication from localStorage
  const checkAuth = () => { return true; // Auth via axios interceptor
  };


  const fetchData = async () => {
    try {
      const [subsRes, listsRes] = await Promise.all([
        api.get('/newsletter/subscribers'),
        api.get('/newsletter/lists')
      ]);
      setSubscribers(subsRes.data);
      setMailingLists(listsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load subscribers');
    }
    setLoading(false);
  };

  // Get list name by slug
  const getListName = (slug) => {
    const list = mailingLists.find(l => l.slug === slug);
    return list ? list.name : slug;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subscriber?')) return;
    try {
      const pwd = getAdminPassword();
      await axios.delete(`${API}/newsletter/subscribers/${id}?password=${encodeURIComponent(pwd)}`);
      toast.success('Subscriber deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleExportCSV = () => {
    const confirmedOnly = subscribers.filter(s => s.status === 'confirmed');
    const csv = ['Email,Status,Lists,Subscribed Date,Confirmed Date'];
    confirmedOnly.forEach(sub => {
      const subscribedDate = sub.created_at || sub.subscribed_at;
      const lists = (sub.lists || []).join(';');
      csv.push(`${sub.email},${sub.status},"${lists}",${subscribedDate ? new Date(subscribedDate).toLocaleDateString() : ''},${sub.confirmed_at ? new Date(sub.confirmed_at).toLocaleDateString() : ''}`);
    });
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newsletter_subscribers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported (confirmed subscribers only)!');
  };

  const handleApprove = async (subId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put('/newsletter/subscribers/' + subId + '/approve?token=' + token);
      toast.success('Subscriber approved!');
      fetchSubscribers();
    } catch (err) {
      toast.error('Failed to approve');
    }
  };

  const handleAddSubscriber = async () => {
    if (!newEmail) return;
    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/newsletter/subscribers/add?token=' + token, {
        email: newEmail,
        lists: newLists
      });
      toast.success('Subscriber added!');
      setNewEmail('');
      setShowAddForm(false);
      fetchSubscribers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const filteredSubscribers = filter === 'all' 
    ? subscribers 
    : subscribers.filter(s => s.status === filter);

  const confirmedCount = subscribers.filter(s => s.status === 'confirmed').length;
  const pendingCount = subscribers.filter(s => s.status === 'pending').length;

  if (false) { // checkAuth removed
    // return null;
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="newsletter-admin-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate('/spoleto/dashboard')}
              className="text-[#0000FF] flex items-center gap-2 mb-4 hover:gap-3 transition-all text-sm"
            >
              <ChevronRight size={16} className="rotate-180" />
              Back to Portal
            </button>
            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
              <Mail className="text-[#0000FF]" />
              Newsletter Subscribers
            </h1>
            <div className="flex gap-4 mt-2 text-sm">
              <span className="text-green-600">{confirmedCount} confirmed</span>
              <span className="text-yellow-600">{pendingCount} pending</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/spoleto/mailing-lists')}
              className="btn-secondary flex items-center gap-2"
              data-testid="manage-lists-btn"
            >
              <List size={16} />
              Manage Lists
            </button>
            <button
              onClick={() => navigate('/spoleto/broadcast')}
              className="btn-primary flex items-center gap-2"
              data-testid="send-broadcast-btn"
            >
              <Send size={18} />
              Send Email
            </button>
            <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <UserPlus size={16} />
                Add Subscriber
              </button>
              {confirmedCount > 0 && (
              <button
                onClick={handleExportCSV}
                className="btn-secondary flex items-center gap-2"
                data-testid="export-csv-btn"
              >
                <Download size={18} />
                Export CSV
              </button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['all', 'confirmed', 'pending'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                filter === f 
                  ? 'bg-[#0000FF] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:text-gray-900'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-600">Loading...</p>
          </div>
        ) : filteredSubscribers.length === 0 ? (
          <div className="bg-white border border-gray-200 p-12 text-center">
            <Users size={48} className="mx-auto mb-4 text-gray-600 opacity-50" />
            <p className="text-gray-600">No {filter !== 'all' ? filter : ''} subscribers yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 text-xs font-mono uppercase text-gray-600">
              <span className="col-span-1">#</span>
              <span className="col-span-4">Email</span>
              <span className="col-span-3">Lists</span>
              <span className="col-span-1">Status</span>
              <span className="col-span-2">Date</span>
              <span className="col-span-1 text-right">Action</span>
            </div>
            {filteredSubscribers.map((sub, idx) => (
              <div
                key={sub.id}
                className="grid grid-cols-12 gap-4 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors items-center"
                data-testid={`subscriber-${sub.id}`}
              >
                <span className="col-span-1 text-gray-600 font-mono">{idx + 1}</span>
                <span className="col-span-4 font-medium truncate">{sub.email}</span>
                <div className="col-span-3 flex flex-wrap gap-1">
                  {(sub.lists || []).map(listSlug => (
                    <span 
                      key={listSlug}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0000FF]/20 text-[#0000FF] text-xs rounded"
                    >
                      <Tag size={10} />
                      {getListName(listSlug)}
                    </span>
                  ))}
                  {(!sub.lists || sub.lists.length === 0) && (
                    <span className="text-xs text-gray-600">None</span>
                  )}
                </div>
                <span className={`col-span-1 text-xs font-bold uppercase ${
                  sub.status === 'confirmed' ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {sub.status}
                </span>
                <span className="col-span-2 text-sm text-gray-600 font-mono">
                  {new Date(sub.created_at || sub.subscribed_at).toLocaleDateString()}
                </span>
                <div className="col-span-1 text-right">
                  <button
                    onClick={() => handleDelete(sub.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                    data-testid={`delete-subscriber-${sub.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterAdminPage;
